// components/portal/useTableFocusRuntimePublication.ts
'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import type { EntityType, InventoryEntityType } from '#types/types';
import {
  buildTableFocusRuntimePayload,
  getSessionTableFocusViewForEntityType,
  stripFocusRuntimeUpdatedAt,
  toFocusRuntimeMap,
} from '#lib/leasing/session-focus';
import { stableSerialize } from './useInventoryList';

/**
 * Publishes "the table the user is looking at" into the active session runtime, under
 * focusRuntimeByKey['table:<view>']. Sole writer of that key for the list stack: Table.tsx and
 * Grid.tsx call this instead of each keeping their own copy of it.
 *
 * `enabled` must be false for an embedded list (locked filters, compact preview): such a list is
 * a scoped sub-view, not the session's table, and publishing it describes to the agent a list the
 * user is not looking at.
 */
export function useTableFocusRuntimePublication({
  entityType,
  presentationMode,
  viewFilters,
  pageSize,
  visibleItemIds,
  enabled,
}: {
  entityType: InventoryEntityType;
  presentationMode: 'table' | 'grid';
  viewFilters: unknown;
  pageSize: number;
  visibleItemIds: string[];
  enabled: boolean;
}): void {
  const { kernel } = useKernel();
  const activeChatSessionId = useKernelStore((state) => state.activeChatSessionId);

  // InventoryEntityType is not a strict subset of EntityType (TimelineEvent has no EntityType
  // twin). Those extra types simply resolve to no table focus view, so the payload is never built
  // for them; the cast is the single place the two unions are bridged.
  const focusEntityType = entityType as EntityType;
  const tableFocusView = useMemo(
    () => getSessionTableFocusViewForEntityType(focusEntityType),
    [focusEntityType]
  );
  const focusRuntimeKey = tableFocusView ? `table:${tableFocusView}` : '';
  const payload = useMemo(
    () =>
      tableFocusView
        ? buildTableFocusRuntimePayload({
            entityType: focusEntityType,
            tableView: tableFocusView,
            presentationMode,
            viewFilters,
            pageSize,
            visibleIds: visibleItemIds,
          })
        : null,
    [focusEntityType, pageSize, presentationMode, tableFocusView, viewFilters, visibleItemIds]
  );
  const payloadSignature = useMemo(() => stableSerialize(payload), [payload]);
  const lastAppliedSignatureRef = useRef<string>('');

  useEffect(() => {
    if (!enabled || !activeChatSessionId || !focusRuntimeKey || !payload) return;
    const k = kernel.current;
    if (!k) return;
    const writeSignature = `${activeChatSessionId}|${focusRuntimeKey}|${payloadSignature}`;
    if (lastAppliedSignatureRef.current === writeSignature) return;

    const runtime = k.readEntityRuntimeState('ChatSession', activeChatSessionId);
    const existingMap = toFocusRuntimeMap(runtime.focusRuntimeByKey);
    if (stableSerialize(stripFocusRuntimeUpdatedAt(existingMap[focusRuntimeKey])) === payloadSignature) {
      lastAppliedSignatureRef.current = writeSignature;
      return;
    }

    k.patchEntityRuntimeState('ChatSession', activeChatSessionId, {
      focusRuntimeByKey: {
        ...existingMap,
        [focusRuntimeKey]: { ...payload, updatedAt: Date.now() },
      },
    });
    lastAppliedSignatureRef.current = writeSignature;
  }, [activeChatSessionId, enabled, focusRuntimeKey, kernel, payload, payloadSignature]);
}
